/**
 * Die Seite "Zahlungsvorlagen" - Einsehen, Bearbeiten, Loeschen und Anlegen der Zahlungsvorlagen
 * (`payment_templates`). Am Lieferanten werden sie nur noch AUSGEWAEHLT (Freigaben und Standard,
 * `Suppliers/ZahlungszieleFeld.tsx`); jede Freigabezeile dort verlinkt direkt auf genau eine
 * Vorlage dieser Seite (`/zahlungsvorlagen/<id>`).
 *
 * Neubau ohne Bestandsvorbild (`index.html` kennt keine Zahlungsvorlagen-Verwaltung), Fachgrundlage
 * `docs/superpowers/specs/2026-09-17-zahlungsplan-katalog-design.md`, "Vorlage und Rate": Bearbeiten
 * wirkt nur auf KUENFTIGE Uebernahmen, eine Bestellung traegt ihre eigene Kopie der Raten.
 *
 * Name einer Vorlage = "[Kommentar] - [Titel aus den Raten]" (`lib/zahlungsvorlagenTitel.ts`); nur
 * der Kommentar ist frei pflegbar. Lesen steht allen Rollen offen (GET /api/payment-templates ohne
 * Recht), Anlegen/Aendern/Loeschen verlangt `manage_suppliers` - ohne das Recht sind die Knoepfe
 * gesperrt, nicht verborgen, damit die Seite dieselbe Antwort gibt wie die Lieferantenmaske.
 *
 * Die Auswahl lebt in der ADRESSE (`ansicht.zahlungsvorlagen.id`), nicht in einem eigenen
 * `useState`: so funktionieren Tieflink, Zurueck-Taste und Verlinken.
 *
 * LIEFERANTENNAMEN: `GET /api/payment-templates` liefert je Lieferant `kLieferant`, `isDefault`, `shortName`
 * (aus `supplier_extras`, oft leer) und `firma`: den JTL-Firmennamen (`tlieferant.cFirma`), den die Leute
 * kennen. Das Backend liest ihn in EINEM Rundlauf fuer alle Lieferanten aller Vorlagen; die Seite loest
 * nichts mehr selbst auf (kein Store-Abgleich, kein Einzelabruf je Lieferant). `firma` ist `null`, wenn JTL
 * den Lieferanten nicht kennt oder nicht erreichbar/konfiguriert ist. Anzeige: Firma, sonst `shortName`,
 * sonst "Unbekannter Lieferant"; die ID erscheint nirgends als Text.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { ApiFehler, KonfliktFehler } from "../../api/basis.ts";
import {
  ladeVorlagen,
  vorlageAendern,
  vorlageLoeschen,
  type PaymentTemplateRow,
} from "../../api/zahlungsvorlagen.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { NutzerFehler, fehlertext } from "../../lib/fehlertext.ts";
import { frischeVorgabe, type Ansicht } from "../../state/adresse.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { ratenTitel, vorlagenName } from "../../lib/zahlungsvorlagenTitel.ts";
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { NeueVorlageDialog } from "../Suppliers/NeueVorlageDialog.tsx";
import { Ikone } from "../Ikone.tsx";
import { Verweis } from "../Verweis.tsx";
import {
  VorlagenEditor,
  entwurfAusVorlage,
  pruefeEntwurf,
  type VorlagenEntwurf,
} from "./VorlagenEditor.tsx";

type VorlagenLieferant = NonNullable<PaymentTemplateRow["suppliers"]>[number];

export interface ZahlungsvorlagenSeiteProps {
  rechte: ReadonlySet<string>;
  /** Der vollstaendige, frische Vorlagenbestand nach jeder Aenderung - haelt den Store aktuell. */
  onVorlagenGeaendert?: (vorlagen: PaymentTemplateRow[]) => void;
}

/** Das Ziel eines Links auf eine Vorlage dieser Seite - die einzige Stelle, die die Ansicht baut. */
export function vorlagenAnsicht(id: string | null): Ansicht {
  return { ...frischeVorgabe(), seite: "paymentTemplates", zahlungsvorlagen: { id } };
}

function lieferantenAnsicht(kLieferant: number): Ansicht {
  return { ...frischeVorgabe(), seite: "suppliers", lieferanten: { q: "", id: String(kLieferant) } };
}

function alsText(wert: unknown): string | null {
  return typeof wert === "string" && wert.trim() !== "" ? wert.trim() : null;
}

/** Der Anzeigename: JTL-Firmenname, sonst Kurzname, sonst "Unbekannter Lieferant" - nie die ID. */
export function lieferantenName(firma: string | null | undefined, shortName: string | null): string {
  return alsText(firma) ?? alsText(shortName) ?? "Unbekannter Lieferant";
}

/** Standard zuerst, dann alphabetisch nach dem Anzeigenamen. */
function sortiereLieferanten(liste: readonly VorlagenLieferant[]): VorlagenLieferant[] {
  const name = (l: VorlagenLieferant): string => lieferantenName(l.firma, l.shortName);
  return [...liste].sort(
    (a, b) =>
      Number(b.isDefault) - Number(a.isDefault) || name(a).localeCompare(name(b), "de", { sensitivity: "base" }),
  );
}

function LieferantenName({ lieferant }: { lieferant: VorlagenLieferant }): JSX.Element {
  return <span className="vorlagen-lieferant-name">{lieferantenName(lieferant.firma, lieferant.shortName)}</span>;
}

const BALKENFARBEN = 4;

export function ZahlungsvorlagenSeite({
  rechte,
  onVorlagenGeaendert,
}: ZahlungsvorlagenSeiteProps): JSX.Element {
  const { ansicht, setzeAnsicht } = useAnsicht();
  const darfSchreiben = hatRecht(rechte, "manage_suppliers");
  const ausgewaehlteId = ansicht.zahlungsvorlagen.id;
  const [vorlagen, setVorlagen] = useState<PaymentTemplateRow[] | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const [suche, setSuche] = useState("");
  const meldeRef = useRef(onVorlagenGeaendert);
  meldeRef.current = onVorlagenGeaendert;

  const uebernehmen = useCallback((liste: PaymentTemplateRow[]): void => {
    setVorlagen(liste);
    meldeRef.current?.(liste);
  }, []);

  const neuLaden = useCallback(async (): Promise<PaymentTemplateRow[] | null> => {
    try {
      const geladen = await ladeVorlagen();
      // NutzerFehler: der Satz ist fuer den Nutzer gedacht und soll nicht im Sammeltext untergehen.
      if (!Array.isArray(geladen)) throw new NutzerFehler("Unerwartete Antwort des Servers.");
      setLadefehler(null);
      uebernehmen(geladen);
      return geladen;
    } catch (grund) {
      // Die Fehlerflaeche gab es schon (R2): neu ist der Text. Der Satz zum Scheitern wird zum Kontext; ergibt
      // `fehlertext` `null` (Abbruch), bleibt der Satz allein, damit die Flaeche nicht wie "laedt" aussieht.
      const kontext = "Die Zahlungsvorlagen konnten nicht geladen werden.";
      setLadefehler(fehlertext(grund, kontext) ?? kontext);
      return null;
    }
  }, [uebernehmen]);

  useEffect(() => {
    void neuLaden();
  }, [neuLaden]);

  const waehle = (id: string | null): void => setzeAnsicht({ zahlungsvorlagen: { id } });
  const ausgewaehlt = vorlagen?.find((v) => v.id === ausgewaehlteId) ?? null;

  const gesucht = suche.trim().toLocaleLowerCase("de");
  const sichtbar =
    vorlagen === null
      ? []
      : vorlagen.filter(
          (v) =>
            gesucht === "" ||
            `${v.comment ?? ""} ${ratenTitel(v.rows)}`.toLocaleLowerCase("de").includes(gesucht),
        );

  return (
    <div className="vorlagen-shell">
      <Seitenkopf
        titel="Zahlungsvorlagen"
        unterzeile="Raten und Fälligkeiten — am Lieferanten werden sie nur noch ausgewählt"
      />
      {ladefehler !== null ? (
        <p className="suppliers-meldung" role="alert">
          {ladefehler}
        </p>
      ) : null}
      <div className="vorlagen-layout">
        <section className="vorlagen-liste" aria-label="Alle Zahlungsvorlagen">
          <div className="vorlagen-liste-kopf">
            <h2>Alle Vorlagen</h2>
            <button
              type="button"
              className="btn-primary"
              disabled={!darfSchreiben}
              title={darfSchreiben ? undefined : NUR_LESEN_TITEL}
              onClick={() => setAnlegenOffen(true)}
            >
              <Ikone name="circle-plus" /> Neue Vorlage
            </button>
          </div>
          {vorlagen === null && ladefehler === null ? <p className="suppliers-leer">Lädt …</p> : null}
          {vorlagen !== null && vorlagen.length === 0 ? (
            <p className="suppliers-leer">Es gibt noch keine Zahlungsvorlage.</p>
          ) : null}
          {vorlagen !== null && vorlagen.length > 0 ? (
            <>
              <div className="vorlagen-suche">
                <Ikone name="search" />
                <input
                  type="search"
                  aria-label="Vorlagen durchsuchen"
                  placeholder="Kommentar oder Raten suchen …"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                />
              </div>
              {sichtbar.length === 0 ? (
                <p className="suppliers-leer">Keine Vorlage passt zur Suche.</p>
              ) : (
                <ul className="vorlagen-karten">
                  {sichtbar.map((v) => {
                    const anzahl = (v.suppliers ?? []).length;
                    const aktiv = v.id === ausgewaehlteId;
                    return (
                      <li
                        key={v.id}
                        className={aktiv ? "vorlagen-karte aktiv" : "vorlagen-karte"}
                        aria-current={aktiv ? "true" : undefined}
                      >
                        <div className="vorlagen-karte-kopf">
                          <Verweis ziel={vorlagenAnsicht(v.id)} className="vorlagen-link">
                            {(v.comment ?? "").trim() !== "" ? v.comment : "(ohne Kommentar)"}
                          </Verweis>
                          {anzahl > 0 ? (
                            <span
                              className="vorlagen-pille vorlagen-zaehler"
                              title={`${anzahl} ${anzahl === 1 ? "Lieferant hat" : "Lieferanten haben"} diese Vorlage freigegeben`}
                            >
                              <Ikone name="truck" /> {anzahl}
                            </span>
                          ) : null}
                        </div>
                        <div className="vorlagen-karte-raten">{ratenTitel(v.rows)}</div>
                        <RatenBalken rows={v.rows} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : null}
        </section>
        <section className="vorlagen-detail" aria-label="Zahlungsvorlage">
          {ausgewaehlteId === null ? (
            <p className="suppliers-leer">Keine Vorlage gewählt. Wähle links eine Vorlage, um ihre Raten zu sehen.</p>
          ) : vorlagen === null ? null : ausgewaehlt === null ? (
            <p className="suppliers-leer" role="alert">
              Die Zahlungsvorlage „{ausgewaehlteId}“ gibt es nicht (mehr).
            </p>
          ) : (
            <VorlagenDetail
              // Wechselt die Vorlage oder ihre Version (gespeichert, neu geladen), baut React den
              // Editor neu auf - derselbe EINE Ruecksetzweg wie in `LieferantenSeite.tsx`. Bei einem
              // 409 aendert sich weder id noch Version, die Eingaben bleiben also stehen.
              key={`${ausgewaehlt.id}-${ausgewaehlt.rowVersion ?? "x"}`}
              vorlage={ausgewaehlt}
              darfSchreiben={darfSchreiben}
              onGespeichert={(neu) =>
                uebernehmen(
                  (vorlagen ?? []).map((v) =>
                    // GET liefert suppliers/orderCount mit, PATCH nicht - die Verwendung aendert sich
                    // durch das Bearbeiten nicht und bleibt deshalb erhalten.
                    v.id === neu.id ? { ...v, ...neu, suppliers: v.suppliers, orderCount: v.orderCount } : v,
                  ),
                )
              }
              onGeloescht={(id) => {
                uebernehmen((vorlagen ?? []).filter((v) => v.id !== id));
                waehle(null);
              }}
              onNeuLaden={neuLaden}
            />
          )}
        </section>
      </div>
      {anlegenOffen ? (
        <NeueVorlageDialog
          hinweis="Danach lässt sie sich am Lieferanten freigeben."
          onSchliessen={() => setAnlegenOffen(false)}
          onAngelegt={async (neu) => {
            uebernehmen([...(vorlagen ?? []), { ...neu, suppliers: [], orderCount: 0 }]);
            setAnlegenOffen(false);
            waehle(neu.id);
          }}
        />
      ) : null}
    </div>
  );
}

/** Der schmale gestapelte Balken unter einer Karte: ein Segment je Rate, Breite = Prozentanteil. */
function RatenBalken({ rows }: { rows: PaymentTemplateRow["rows"] }): JSX.Element | null {
  const anteile = rows.map((r) => {
    const wert = Number(String(r.pct ?? "").replace(",", "."));
    return Number.isFinite(wert) && wert > 0 ? wert : 0;
  });
  if (anteile.every((a) => a === 0)) return null;
  return (
    <div className="vorlagen-balken" aria-hidden="true">
      {anteile.map((anteil, index) =>
        anteil > 0 ? (
          <span
            key={index}
            className={`vorlagen-balken-teil farbe-${index % BALKENFARBEN}`}
            style={{ flexGrow: anteil }}
          />
        ) : null,
      )}
    </div>
  );
}

interface VorlagenDetailProps {
  vorlage: PaymentTemplateRow;
  darfSchreiben: boolean;
  onGespeichert: (neu: PaymentTemplateRow) => void;
  onGeloescht: (id: string) => void;
  onNeuLaden: () => Promise<PaymentTemplateRow[] | null>;
}

function VorlagenDetail({
  vorlage,
  darfSchreiben,
  onGespeichert,
  onGeloescht,
  onNeuLaden,
}: VorlagenDetailProps): JSX.Element {
  const ausgangsentwurf = useMemo(() => JSON.stringify(entwurfAusVorlage(vorlage)), [vorlage]);
  const [entwurf, setEntwurf] = useState<VorlagenEntwurf>(() => entwurfAusVorlage(vorlage));
  const [fehler, setFehler] = useState<string | null>(null);
  const [konflikt, setKonflikt] = useState(false);
  const [blockierer, setBlockierer] = useState<PaymentTemplateRow["suppliers"] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const sperrTitel = darfSchreiben ? undefined : NUR_LESEN_TITEL;
  const gesperrt = !darfSchreiben || laeuft;
  const geaendert = JSON.stringify(entwurf) !== ausgangsentwurf;
  const name = vorlagenName(vorlage.comment, vorlage.rows);
  const lieferanten = sortiereLieferanten(vorlage.suppliers ?? []);
  const bestellungen = vorlage.orderCount ?? 0;

  async function speichern(): Promise<void> {
    if (gesperrt) return;
    setFehler(null);
    setKonflikt(false);
    setBlockierer(null);
    const ergebnis = pruefeEntwurf(entwurf);
    if (ergebnis.fehler !== null) {
      setFehler(ergebnis.fehler);
      return;
    }
    if (vorlage.rowVersion === undefined) {
      setFehler("Die Version der Vorlage fehlt - bitte neu laden.");
      return;
    }
    setLaeuft(true);
    try {
      onGespeichert(await vorlageAendern(vorlage.id, ergebnis.comment, ergebnis.rows, vorlage.rowVersion));
    } catch (grund) {
      if (grund instanceof KonfliktFehler) {
        setKonflikt(true);
        setFehler("Zwischenzeitlich von jemand anderem geändert – nichts wurde gespeichert.");
      } else {
        // Inline bleibt inline (Spec R2): Text aus `fehlertext()`, der Satz zum Scheitern ist der Kontext.
        setFehler(fehlertext(grund, "Die Vorlage konnte nicht gespeichert werden."));
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function loeschen(): Promise<void> {
    if (gesperrt) return;
    if (!confirm(`Zahlungsvorlage „${name}“ wirklich löschen?`)) return;
    setFehler(null);
    setKonflikt(false);
    setBlockierer(null);
    setLaeuft(true);
    try {
      await vorlageLoeschen(vorlage.id);
      onGeloescht(vorlage.id);
    } catch (grund) {
      if (grund instanceof ApiFehler && grund.status === 409) {
        // Der Server nennt die Lieferanten im Text; die Links dazu kommen aus dem frischen Bestand.
        setFehler(fehlertext(grund));
        const frisch = (await onNeuLaden())?.find((v) => v.id === vorlage.id);
        setBlockierer(frisch?.suppliers ?? vorlage.suppliers ?? []);
      } else {
        setFehler(fehlertext(grund, "Die Vorlage konnte nicht gelöscht werden."));
      }
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="vorlagen-detail-inhalt">
      <h2 className="vorlagen-detail-titel">{name}</h2>
      {!darfSchreiben ? (
        <p className="supplier-extras-hinweis" role="status">
          <Ikone name="triangle-alert" />
          {NUR_LESEN_TITEL}
        </p>
      ) : null}
      <p className="vorlagen-info">
        <Ikone name="info" />
        <span>
          Änderungen wirken nur auf künftige Übernahmen. Bestellungen, die diese Vorlage bereits übernommen haben,
          behalten ihren Plan.
        </span>
      </p>
      <VorlagenEditor
        entwurf={entwurf}
        onChange={setEntwurf}
        gesperrt={gesperrt}
        sperrTitel={sperrTitel}
        kennung={`vorlage-${vorlage.id}`}
        gespeicherterName={name}
      />
      {fehler !== null ? (
        <p className="supplier-extras-fehler" role="alert">
          {fehler}
        </p>
      ) : null}
      {konflikt ? (
        <button type="button" className="btn-sec" onClick={() => void onNeuLaden()}>
          <Ikone name="refresh-cw" /> Aktuellen Stand laden
        </button>
      ) : null}
      {blockierer != null && blockierer.length > 0 ? (
        <div className="vorlagen-blockierer" role="group" aria-label="Noch freigegeben bei">
          <strong>Noch freigegeben bei:</strong>
          <ul>
            {sortiereLieferanten(blockierer).map((l) => (
              <li key={l.kLieferant}>
                <Verweis ziel={lieferantenAnsicht(l.kLieferant)}>
                  <LieferantenName lieferant={l} />
                </Verweis>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="vorlagen-aktionen">
        <button
          type="button"
          className="btn-primary"
          disabled={gesperrt || !geaendert}
          title={sperrTitel ?? (geaendert ? undefined : "Keine Änderungen zu speichern")}
          onClick={() => void speichern()}
        >
          Speichern
        </button>
        <button
          type="button"
          className="vorlagen-loeschen"
          disabled={gesperrt}
          title={sperrTitel}
          onClick={() => void loeschen()}
        >
          <Ikone name="trash-2" /> Löschen
        </button>
      </div>
      <section className="vorlagen-verwendung" aria-label="Verwendet bei">
        <h3>Verwendet bei</h3>
        <div className="vorlagen-kacheln">
          <div className="vorlagen-kachel">
            <span className="vorlagen-kachel-zahl">{lieferanten.length}</span>
            <span className="vorlagen-kachel-text">Lieferanten</span>
          </div>
          <div className="vorlagen-kachel">
            <span className="vorlagen-kachel-zahl">{bestellungen}</span>
            <span className="vorlagen-kachel-text">Bestellungen</span>
          </div>
        </div>
        <p className="vorlagen-verwendung-hinweis">
          Bestellungen behalten ihren Plan, auch wenn die Vorlage geändert oder gelöscht wird.
        </p>
        {lieferanten.length === 0 ? (
          <p className="vorlagen-leer">Noch kein Lieferant hat diese Vorlage freigegeben.</p>
        ) : (
          <ul className="vorlagen-lieferanten">
            {lieferanten.map((l) => (
              <li key={l.kLieferant}>
                <Verweis ziel={lieferantenAnsicht(l.kLieferant)} className="vorlagen-lieferant">
                  <LieferantenName lieferant={l} />
                  {l.isDefault ? <span className="vorlagen-pille vorlagen-standard">Standard</span> : null}
                  <span className="vorlagen-lieferant-pfeil">
                    <Ikone name="arrow-right" />
                  </span>
                </Verweis>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
